import React, { memo, useCallback, useContext } from 'react';
import { Typography, TypographyVariant } from 'components/Typography';
import type { List, Collection, Normalized } from 'lib/LobbyEntities';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import RouterLink from 'components/RouterLink';
import Lobby from 'modules/Lobby';
import Analytics from 'modules/Analytics';
import withLazyStyle from 'components/LazyStyle';
import { type Mapping } from '../../../Builder';
import DynamicLobbyContext from '../../../DynamicLobbyContext';
import GamesList from './components/GamesList';
import OffersList from './components/OffersList';
import style from './rowList.css?lazy';

interface RowListAdapterProps {
  list: List;
  mapping: Mapping;
  position: number;
}

interface RowListProps extends RowListAdapterProps {
  collection: Normalized<Collection>;
  viewAllURL: string;
  trackSectionScrolled: () => void;
}

export const RowList = ({
  list,
  mapping,
  collection,
  viewAllURL,
  position,
  trackSectionScrolled
}: RowListProps) => {
  const { showTitle, showSeeAll, seeAllText } = list;
  const showTopBar = showTitle || showSeeAll;
  // TODO fix when BE is ready to handle showSeeAll flag for list with contentType
  const isViewAllLinkVisible = list.contentType === 'offer' || showSeeAll;

  return (
    <div
      className="dynamicLobby__rowList"
      data-amp-id="gamelist"
      data-amp-list-ref={collection.refName}
      data-amp-list-position={position}
    >
      {showTopBar && (
        <div className="dynamicLobby__rowList-topBar">
          {showTitle && (
            <Typography variant={TypographyVariant.BodyMdStrong}>
              <span>{collection.title}</span>
            </Typography>
          )}
          {isViewAllLinkVisible && (
            <Typography variant={TypographyVariant.BodySmStrong}>
              <RouterLink href={viewAllURL} className="dynamicLobby__rowList-viewAll">
                {seeAllText || 'View all'}
              </RouterLink>
            </Typography>
          )}
        </div>
      )}
      {list.contentType === 'game' && (
        <GamesList list={list} mapping={mapping} trackSectionScrolled={trackSectionScrolled} />
      )}
      {list.contentType === 'offer' && (
        <OffersList
          collection={collection}
          list={list}
          mapping={mapping}
          trackSectionScrolled={trackSectionScrolled}
        />
      )}
    </div>
  );
};

const RowListAdapter = ({ list, mapping, position }: RowListAdapterProps) => {
  const dispatch = useAppDispatch();
  const { section, category } = useContext(DynamicLobbyContext);
  const collection = useAppSelector((state) =>
    Lobby.selectors.getEntityByKey(state, list.collection.__access)
  ) as Normalized<Collection> | null;
  const trackSectionScrolled = useCallback(() => {
    if (!collection) {
      return;
    }

    dispatch(
      Analytics.actions.sectionScrolled({
        refName: collection.refName,
        type: 'Dynamic lobby',
        name: collection.title,
        section: section,
        category: category,
        position
      })
    );
  }, [dispatch, collection, category, section, position]);

  if (!list || !list.children || !collection || list.children.length === 0) {
    return null;
  }

  const viewAllURL =
    list.contentType === 'game'
      ? `/secure/lobby/${section}/${category}/${collection.refName}`
      : '/secure/lobby/rewards';

  return (
    <RowList
      list={list}
      collection={collection}
      mapping={mapping}
      viewAllURL={viewAllURL}
      position={position}
      trackSectionScrolled={trackSectionScrolled}
    />
  );
};

export default memo(withLazyStyle(style)(RowListAdapter));
